import React, { useEffect, useState } from "react";
import {
  CardBody,
  Col,
  Row,
  Input,
  Label,
  Button,
  FormFeedback,
  Alert,
  Spinner,
  Form,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import imageLogo from "../../../assets/orderingSystem/login-bg.jpg";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { resetPassword } from "../redux/slices/customerAuthSlice";

const CustomerResetPassword = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { orgcode } = useParams();
  const otpFromState = location.state?.otp || "";

  const { emailForOtp, loading, error } = useSelector(
    (s) => s.customerAuth || {}
  );
  const [errorMsg, setErrorMsg] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      emailAddress: emailForOtp || "",
      otp: otpFromState,
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      emailAddress: Yup.string()
        .email("Please enter a valid email")
        .required("Email is required"),
      otp: Yup.string()
        .length(6, "OTP must be 6 digits")
        .matches(/^[0-9]{6}$/, "OTP must contain only numbers")
        .required("OTP is required"),
      password: Yup.string()
        .min(6, "Password must be at least 6 characters")
        .required("Password is required"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password"), null], "Passwords must match")
        .required("Confirm password is required"),
    }),
    onSubmit: async (values) => {
      try {
        setErrorMsg(null);
        const payload = {
          emailAddress: values.emailAddress,
          otp: values.otp,
          password: values.password,
          confirmPassword: values.confirmPassword,
        };
        const res = await dispatch(resetPassword(payload));
        if (res?.type && res.type.includes("fulfilled")) {
          navigate(`/${orgcode}/customer-login`);
        } else {
          const msg = res?.payload?.message || res?.payload || "Reset failed";
          setErrorMsg(msg);
        }
      } catch (err) {
        setErrorMsg("Reset failed. Please try again.");
        console.error("Reset password error:", err);
      }
    },
  });

  useEffect(() => {
    if (errorMsg) {
      const t = setTimeout(() => setErrorMsg(null), 4000);
      return () => clearTimeout(t);
    }
  }, [errorMsg]);

  document.title = "Reset Password - Ordering System";

  return (
    <React.Fragment>
      <div className="vh-100">
        <Row className="h-100 g-0">
          <Col
            lg={{ size: 5, order: 2 }}
            md={{ size: 5, order: 2 }}
            xs={{ size: 12, order: 1 }}
            sm={{ size: 12, order: 1 }}
            xl={{ size: 6, order: 2 }}
            className="h-100 d-none d-sm-none d-md-block d-lg-block"
            style={{ backgroundColor: "#0f403b" }}
          >
            <div className="py-5 d-flex flex-column justify-content-center align-items-center h-100">
              <CardBody
                className="p-4"
                style={{ maxWidth: "480px", width: "100%" }}
              >
                <div className="text-center mb-4">
                  <h5 className="text-white">Reset your password</h5>
                  <p className="text-white-50">
                    Enter the OTP and a new password to reset your account
                    password.
                  </p>
                </div>

                {errorMsg && <Alert color="danger">{errorMsg}</Alert>}
                {error && (
                  <Alert color="danger">
                    {typeof error === "string" ? error : JSON.stringify(error)}
                  </Alert>
                )}

                <div className="p-2 py-3">
                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      validation.handleSubmit();
                    }}
                  >
                    <div className="mb-3">
                      <Input
                        name="emailAddress"
                        className="form-control p-3"
                        placeholder="Enter email"
                        type="email"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.emailAddress || ""}
                        invalid={
                          validation.touched.emailAddress &&
                          validation.errors.emailAddress
                        }
                        disabled={!!emailForOtp}
                      />
                      {validation.touched.emailAddress &&
                        validation.errors.emailAddress && (
                          <FormFeedback>
                            {validation.errors.emailAddress}
                          </FormFeedback>
                        )}
                    </div>

                    <div className="mb-3">
                      <Input
                        name="otp"
                        className="form-control p-3"
                        placeholder="Enter 6-digit OTP"
                        type="text"
                        onChange={(e) => {
                          const v = e.target.value.replace(/[^0-9]/g, "");
                          validation.setFieldValue("otp", v);
                        }}
                        onBlur={validation.handleBlur}
                        value={validation.values.otp || ""}
                        invalid={
                          validation.touched.otp && validation.errors.otp
                        }
                      />
                      {validation.touched.otp && validation.errors.otp && (
                        <FormFeedback>{validation.errors.otp}</FormFeedback>
                      )}
                    </div>

                    <div className="mb-3 position-relative auth-pass-inputgroup">
                      <Input
                        name="password"
                        value={validation.values.password || ""}
                        type={showPassword ? "text" : "password"}
                        className="form-control pe-5 p-3"
                        placeholder="Enter new password"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={
                          validation.touched.password &&
                          validation.errors.password
                        }
                      />
                      <button
                        className="btn btn-link position-absolute end-0 top-0 pt-3 text-decoration-none text-muted"
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        <i className="ri-eye-fill align-middle"></i>
                      </button>
                      {validation.touched.password &&
                        validation.errors.password && (
                          <FormFeedback type="invalid">
                            {validation.errors.password}
                          </FormFeedback>
                        )}
                    </div>

                    <div className="mb-3 position-relative auth-pass-inputgroup">
                      <Input
                        name="confirmPassword"
                        value={validation.values.confirmPassword || ""}
                        type={showConfirm ? "text" : "password"}
                        className="form-control pe-5 p-3"
                        placeholder="Confirm new password"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={
                          validation.touched.confirmPassword &&
                          validation.errors.confirmPassword
                        }
                      />
                      <button
                        className="btn btn-link position-absolute end-0 top-0 pt-3 text-decoration-none text-muted"
                        type="button"
                        onClick={() => setShowConfirm(!showConfirm)}
                      >
                        <i className="ri-eye-fill align-middle"></i>
                      </button>
                      {validation.touched.confirmPassword &&
                        validation.errors.confirmPassword && (
                          <FormFeedback type="invalid">
                            {validation.errors.confirmPassword}
                          </FormFeedback>
                        )}
                    </div>

                    <div className="mb-3">
                      <Button
                        disabled={loading}
                        color="success"
                        className="btn btn-success w-100"
                        type="submit"
                      >
                        {loading ? (
                          <>
                            <Spinner size="sm" className="me-2" /> Resetting...
                          </>
                        ) : (
                          "Reset Password"
                        )}
                      </Button>
                    </div>

                    <div className="text-center text-white">
                      <p className="mb-0">
                        Back to{" "}
                        <Link
                          to={`/${orgcode}/customer-login`}
                          className="text-warning"
                        >
                          Sign in
                        </Link>
                      </p>
                    </div>
                  </Form>
                </div>
              </CardBody>
            </div>
          </Col>

          <Col
            lg={{ size: 7, order: 1 }}
            md={{ size: 7, order: 1 }}
            xl={{ size: 6, order: 1 }}
            xs={{ size: 12, order: 2 }}
            sm={{ size: 12, order: 2 }}
            className="position-relative h-100 d-none d-sm-none d-md-flex d-lg-flex"
          >
            <img
              src={imageLogo}
              alt="Background"
              className="w-100 h-100"
              style={{ objectFit: "cover" }}
            />
            <div className="login-overlay d-flex flex-column justify-content-center align-items-start position-absolute w-100 h-100">
              <div className="overlay-content w-100 px-4">
                <h1 className="fw-bold display-5 mb-4 text-white">
                  Public Ordering System
                </h1>
                <p className="text-white-50">
                  Fast, simple and secure ordering for customers.
                </p>
              </div>
            </div>
          </Col>

          {/* Register Form - Small screens */}
          <Col
            lg={{ size: 5, order: 2 }}
            xs={{ size: 12, order: 1 }}
            sm={{ size: 12, order: 1 }}
            xl={{ size: 6, order: 2 }}
            className="h-100 d-block d-sm-block d-md-none d-lg-none position-relative"
          >
            <img
              src={imageLogo}
              alt="Public Ordering System"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                position: "absolute",
                top: 0,
                left: 0,
              }}
            />
            <div
              className="py-5 d-flex flex-column justify-content-center align-items-center h-100"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CardBody
                className="p-4"
                style={{ maxWidth: "480px", width: "100%" }}
              >
                <div className="text-center mb-4">
                  <h5 className="text-white">Reset your password</h5>
                  <p className="text-white-50">
                    Enter the OTP and a new password to reset your account
                    password.
                  </p>
                </div>

                {errorMsg && <Alert color="danger">{errorMsg}</Alert>}
                {error && (
                  <Alert color="danger">
                    {typeof error === "string" ? error : JSON.stringify(error)}
                  </Alert>
                )}

                <div className="p-2 py-3">
                  <Form
                    onSubmit={(e) => {
                      e.preventDefault();
                      validation.handleSubmit();
                    }}
                  >
                    <div className="mb-3">
                      <Input
                        name="emailAddress"
                        className="form-control p-3"
                        placeholder="Enter email"
                        type="email"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        value={validation.values.emailAddress || ""}
                        invalid={
                          validation.touched.emailAddress &&
                          validation.errors.emailAddress
                        }
                        disabled={!!emailForOtp}
                      />
                      {validation.touched.emailAddress &&
                        validation.errors.emailAddress && (
                          <FormFeedback>
                            {validation.errors.emailAddress}
                          </FormFeedback>
                        )}
                    </div>

                    <div className="mb-3">
                      <Input
                        name="otp"
                        className="form-control p-3"
                        placeholder="Enter 6-digit OTP"
                        type="text"
                        onChange={(e) => {
                          const v = e.target.value.replace(/[^0-9]/g, "");
                          validation.setFieldValue("otp", v);
                        }}
                        onBlur={validation.handleBlur}
                        value={validation.values.otp || ""}
                        invalid={
                          validation.touched.otp && validation.errors.otp
                        }
                      />
                      {validation.touched.otp && validation.errors.otp && (
                        <FormFeedback>{validation.errors.otp}</FormFeedback>
                      )}
                    </div>

                    <div className="mb-3 position-relative auth-pass-inputgroup">
                      <Input
                        name="password"
                        value={validation.values.password || ""}
                        type={showPassword ? "text" : "password"}
                        className="form-control pe-5 p-3"
                        placeholder="Enter new password"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={
                          validation.touched.password &&
                          validation.errors.password
                        }
                      />
                      <button
                        className="btn btn-link position-absolute end-0 top-0 pt-3 text-decoration-none text-muted"
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        <i className="ri-eye-fill align-middle"></i>
                      </button>
                      {validation.touched.password &&
                        validation.errors.password && (
                          <FormFeedback type="invalid">
                            {validation.errors.password}
                          </FormFeedback>
                        )}
                    </div>

                    <div className="mb-3 position-relative auth-pass-inputgroup">
                      <Input
                        name="confirmPassword"
                        value={validation.values.confirmPassword || ""}
                        type={showConfirm ? "text" : "password"}
                        className="form-control pe-5 p-3"
                        placeholder="Confirm new password"
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={
                          validation.touched.confirmPassword &&
                          validation.errors.confirmPassword
                        }
                      />
                      <button
                        className="btn btn-link position-absolute end-0 top-0 pt-3 text-decoration-none text-muted"
                        type="button"
                        onClick={() => setShowConfirm(!showConfirm)}
                      >
                        <i className="ri-eye-fill align-middle"></i>
                      </button>
                      {validation.touched.confirmPassword &&
                        validation.errors.confirmPassword && (
                          <FormFeedback type="invalid">
                            {validation.errors.confirmPassword}
                          </FormFeedback>
                        )}
                    </div>

                    <div className="mb-3">
                      <Button
                        disabled={loading}
                        color="success"
                        className="btn btn-success w-100"
                        type="submit"
                      >
                        {loading ? (
                          <>
                            <Spinner size="sm" className="me-2" /> Resetting...
                          </>
                        ) : (
                          "Reset Password"
                        )}
                      </Button>
                    </div>

                    <div className="text-center text-white">
                      <p className="mb-0">
                        Back to{" "}
                        <Link
                          to={`/${orgcode}/customer-login`}
                          className="text-warning"
                        >
                          Sign in
                        </Link>
                      </p>
                    </div>
                  </Form>
                </div>
              </CardBody>

              <footer className="mt-auto w-100">
                <div className="container">
                  <Row>
                    <Col xs={12}>
                      <div className="text-center">
                        <p className="mb-0 text-muted">
                          &copy; {new Date().getFullYear()} Danfe Solution
                        </p>
                      </div>
                    </Col>
                  </Row>
                </div>
              </footer>
            </div>
          </Col>
        </Row>
      </div>

      <style>{`
        .login-overlay { top: 0; left: 0; }
        .form-control { border-radius: 8px; border: 1px solid #ddd; }
        .form-control:focus { border-color: #28a745; box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25); }
        .btn-success { background-color: #28a745; border-color: #28a745; border-radius: 8px; font-weight: 600; }
      `}</style>
    </React.Fragment>
  );
};

export default CustomerResetPassword;
